import { useSyncExternalStore } from 'react';

import { type Shift } from '@libraries/typescript/api_client';

// Order the tray renders sections in. Declared first so the type below is
// exhaustive by construction.
const REFERENCE_TYPE_ORDER = [
    'shift',
    'callout',
    'client',
    'caregiver',
    'playbook',
] as const;

type ChatReferenceType = (typeof REFERENCE_TYPE_ORDER)[number];

type StagedShiftReference = {
    readonly type: 'shift';
    readonly id: string;
    readonly shift: Shift;
};

type StagedNamedReference = {
    readonly type: Exclude<ChatReferenceType, 'shift'>;
    readonly id: string;
    readonly displayName: string;
};

type StagedReference = StagedShiftReference | StagedNamedReference;

type ChatReferenceState = {
    readonly contextKey: string | null;
    readonly orderedReferences: ReadonlyArray<StagedReference>;
    readonly idsByType: ReadonlyMap<ChatReferenceType, ReadonlySet<string>>;
};

const EMPTY_IDS: ReadonlySet<string> = new Set();

const EMPTY_STATE: ChatReferenceState = {
    contextKey: null,
    orderedReferences: [],
    idsByType: new Map(),
};

let state = EMPTY_STATE;
const listeners = new Set<() => void>();

function emitChange() {
    for (const listener of listeners) {
        listener();
    }
}

export function buildChatReferenceContextKey(
    organizationId: string | null,
    mode: string,
): string {
    return `${organizationId ?? 'none'}:${mode}`;
}

function isShiftReference(
    reference: StagedReference,
): reference is StagedShiftReference {
    return reference.type === 'shift';
}

// Shifts read chronologically; every other type keeps insertion order via the
// stable sort on an equal key.
function withinTypeSortKey(reference: StagedReference): string {
    return isShiftReference(reference)
        ? `${reference.shift.shift_start_time}|${reference.id}`
        : '';
}

function buildState(
    contextKey: string,
    references: ReadonlyArray<StagedReference>,
): ChatReferenceState {
    const byType = new Map<ChatReferenceType, Array<StagedReference>>();
    for (const reference of references) {
        const group = byType.get(reference.type);
        if (group) {
            group.push(reference);
        } else {
            byType.set(reference.type, [reference]);
        }
    }

    const orderedReferences: Array<StagedReference> = [];
    const idsByType = new Map<ChatReferenceType, ReadonlySet<string>>();
    for (const type of REFERENCE_TYPE_ORDER) {
        const group = byType.get(type);
        if (!group) {
            continue;
        }
        group.sort((left, right) =>
            withinTypeSortKey(left).localeCompare(withinTypeSortKey(right)),
        );
        orderedReferences.push(...group);
        idsByType.set(type, new Set(group.map((reference) => reference.id)));
    }

    return orderedReferences.length === 0
        ? EMPTY_STATE
        : { contextKey, orderedReferences, idsByType };
}

function hasSameOrderedIds(
    left: ReadonlyArray<StagedReference>,
    right: ReadonlyArray<StagedReference>,
): boolean {
    return (
        left.length === right.length &&
        left.every((reference, index) => reference.id === right[index]?.id)
    );
}

function commit(next: ChatReferenceState) {
    if (
        state.contextKey === next.contextKey &&
        hasSameOrderedIds(state.orderedReferences, next.orderedReferences)
    ) {
        return;
    }
    state = next;
    emitChange();
}

function removeIds(candidateIds: Iterable<string>) {
    const candidates = new Set(candidateIds);
    const kept = state.orderedReferences.filter(
        (reference) => !candidates.has(reference.id),
    );
    if (kept.length === state.orderedReferences.length) {
        return;
    }
    state =
        kept.length === 0 || state.contextKey === null
            ? EMPTY_STATE
            : buildState(state.contextKey, kept);
    emitChange();
}

export const chatReferenceStore = {
    getState(): ChatReferenceState {
        return state;
    },
    // Replaces one type's references, leaving other types staged. Snapshots
    // survive the producing page's unmount so the chat tray can render them.
    setTypeSelection(
        contextKey: string,
        type: ChatReferenceType,
        references: ReadonlyArray<StagedReference>,
    ) {
        // Everything staged under another org or mode is stale here.
        const retained =
            state.contextKey === contextKey
                ? state.orderedReferences.filter(
                      (reference) => reference.type !== type,
                  )
                : [];
        commit(buildState(contextKey, [...retained, ...references]));
    },
    addReferences(
        contextKey: string,
        references: ReadonlyArray<StagedReference>,
    ) {
        const retained =
            state.contextKey === contextKey ? state.orderedReferences : [];
        const retainedIds = new Set(retained.map((reference) => reference.id));
        commit(
            buildState(contextKey, [
                ...retained,
                ...references.filter(
                    (reference) => !retainedIds.has(reference.id),
                ),
            ]),
        );
    },
    remove(referenceId: string) {
        removeIds([referenceId]);
    },
    // Removes several ids in one rebuild so an in-flight send can drop the
    // references it sent without wiping ones staged during the send.
    removeMany(referenceIds: ReadonlyArray<string>) {
        removeIds(referenceIds);
    },
    clearType(type: ChatReferenceType) {
        removeIds(
            state.orderedReferences
                .filter((reference) => reference.type === type)
                .map((reference) => reference.id),
        );
    },
    clear() {
        if (state === EMPTY_STATE) {
            return;
        }
        state = EMPTY_STATE;
        emitChange();
    },
    subscribe(listener: () => void) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
};

export function useChatReferences(): ChatReferenceState {
    return useSyncExternalStore(
        chatReferenceStore.subscribe,
        chatReferenceStore.getState,
    );
}

export function referenceIdsOfType(
    referenceState: ChatReferenceState,
    type: ChatReferenceType,
): ReadonlySet<string> {
    return referenceState.idsByType.get(type) ?? EMPTY_IDS;
}

export function stagedShifts(
    referenceState: ChatReferenceState,
): ReadonlyArray<Shift> {
    return referenceState.orderedReferences
        .filter(isShiftReference)
        .map((reference) => reference.shift);
}

export function shiftReference(shift: Shift): StagedShiftReference {
    return { type: 'shift', id: shift.id, shift };
}

export type {
    ChatReferenceState,
    ChatReferenceType,
    StagedReference,
    StagedShiftReference,
};
